/**
 * ResidentHome — property header, quick violation action, active services,
 * recent notifications, my violation statuses.
 */
import { AlertTriangle, Sparkles, ChevronRight, RefreshCw, CalendarDays } from 'lucide-react';
import ResidentPickupSection from './ResidentPickupSection';
import ResidentServiceEventCard from './ResidentServiceEventCard';
import WelcomeStrip from '../../components/WelcomeStrip';

const GOLD = '#C9A962';

const STATUS_COLORS = {
  submitted: { bg: 'bg-blue-950/50', text: 'text-blue-400', label: 'Submitted' },
  reviewed: { bg: 'bg-amber-950/50', text: 'text-amber-400', label: 'Reviewed' },
  in_progress: { bg: 'bg-purple-950/50', text: 'text-purple-400', label: 'In Progress' },
  resolved: { bg: 'bg-emerald-950/50', text: 'text-emerald-400', label: 'Resolved' },
};

export default function ResidentHome({ home, onSubmitViolation, onRefresh }) {
  if (!home) return null;
  const { property, resident, active_services, upcoming_schedule, notifications, violations } = home;

  return (
    <div className="space-y-5" data-testid="resident-home">
      <WelcomeStrip name={resident?.name} nameColor="#ffffff" testid="resident-home-welcome" />
      {/* Property card */}
      <div className="rounded-2xl p-5 border border-[#C9A962]/40" style={{ background: 'linear-gradient(135deg, #1c1a15, #141414)' }}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-lg font-black uppercase text-white leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{property?.property_name}</p>
            <p className="text-[11px] text-zinc-500 mt-0.5">{[property?.city, property?.state].filter(Boolean).join(', ')}</p>
          </div>
          <button onClick={onRefresh} className="p-1.5 text-zinc-500" data-testid="resident-home-refresh"><RefreshCw className="h-4 w-4" /></button>
        </div>
        <div className="flex gap-2 mt-3">
          <span className="px-2 py-1 rounded bg-zinc-900 text-[10px] font-bold uppercase tracking-wider" style={{ color: GOLD }} data-testid="resident-community-code">
            {property?.community_code}
          </span>
          <span className="px-2 py-1 rounded bg-zinc-900 text-[10px] font-bold uppercase tracking-wider text-zinc-400">
            Unit {resident?.unit_number}
          </span>
        </div>
      </div>

      {/* Quick action */}
      <button onClick={onSubmitViolation}
        className="w-full flex items-center justify-between rounded-2xl px-5 py-4 font-black uppercase tracking-widest text-sm"
        style={{ backgroundColor: GOLD, color: '#1a1a1a' }} data-testid="resident-quick-violation-btn">
        <span className="flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> Submit a Violation</span>
        <ChevronRight className="h-5 w-5" />
      </button>

      {/* Section 2 (iter-140) · Live Service Event card (pre/post) —
          polls the resident-lens endpoint every 30s so the card flips
          from prep instructions to "Service Completed — thank you"
          automatically the moment a field agent submits. */}
      <ResidentServiceEventCard />

      {/* Trash & Recycling pickups (manual + QR scan) */}
      <ResidentPickupSection unitNumber={resident?.unit_number} />

      {/* Static package description */}
      <section>
        <h2 className="text-[11px] font-black uppercase tracking-[0.25em] mb-2" style={{ color: GOLD }}>Your Service Package</h2>
        <div className="space-y-2" data-testid="resident-active-services">
          {(active_services || []).length === 0 && (
            <p className="text-xs text-zinc-600 rounded-xl border border-zinc-800 p-4">Your community's service package details will appear here.</p>
          )}
          {(active_services || []).map((s, i) => (
            <div key={i} className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3.5 flex items-start gap-3">
              <Sparkles className="h-4 w-4 mt-0.5 shrink-0" style={{ color: GOLD }} />
              <div>
                <p className="text-sm font-bold text-white">{s.name}</p>
                <p className="text-[11px] text-zinc-500">{s.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Real upcoming service dates */}
      <section>
        <h2 className="text-[11px] font-black uppercase tracking-[0.25em] mb-2" style={{ color: GOLD }}>Upcoming Services</h2>
        <div className="space-y-2" data-testid="resident-upcoming-schedule">
          {(upcoming_schedule || []).length === 0 && (
            <p className="text-xs text-zinc-600 rounded-xl border border-zinc-800 p-4">No upcoming services scheduled — check back soon.</p>
          )}
          {(upcoming_schedule || []).map((s) => (
            <div key={s.schedule_id} className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3.5 flex items-center gap-3" data-testid={`upcoming-service-${s.schedule_id}`}>
              <CalendarDays className="h-4 w-4 shrink-0" style={{ color: GOLD }} />
              <div className="flex-1">
                <p className="text-sm font-bold text-white">{s.service_type}</p>
              </div>
              <span className="text-[11px] font-bold px-2 py-1 rounded bg-zinc-800" style={{ color: GOLD }}>
                {new Date(`${s.scheduled_date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Recent notifications */}
      <section>
        <h2 className="text-[11px] font-black uppercase tracking-[0.25em] mb-2" style={{ color: GOLD }}>Recent Updates</h2>
        <div className="space-y-2" data-testid="resident-home-notifications">
          {(notifications || []).length === 0 && (
            <p className="text-xs text-zinc-600 rounded-xl border border-zinc-800 p-4">No updates yet.</p>
          )}
          {(notifications || []).slice(0, 5).map((n) => (
            <div key={n.notif_id} className={`rounded-xl border p-3.5 ${n.read ? 'border-zinc-800 bg-zinc-900/40' : 'border-[#C9A962]/50 bg-[#C9A962]/5'}`}>
              <p className="text-sm font-bold text-white">{n.title}</p>
              <p className="text-[11px] text-zinc-400 mt-0.5">{n.message}</p>
              <p className="text-[10px] text-zinc-600 mt-1">{new Date(n.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      </section>

      {/* My violations */}
      <section>
        <h2 className="text-[11px] font-black uppercase tracking-[0.25em] mb-2" style={{ color: GOLD }}>My Violation Reports</h2>
        <div className="space-y-2" data-testid="resident-home-violations">
          {(violations || []).length === 0 && (
            <p className="text-xs text-zinc-600 rounded-xl border border-zinc-800 p-4">You haven't submitted any reports.</p>
          )}
          {(violations || []).map((v) => {
            const st = STATUS_COLORS[v.status] || STATUS_COLORS.submitted;
            return (
              <div key={v.reference} className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3.5" data-testid={`resident-violation-${v.reference}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold text-white">{v.violation_type}</p>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${st.bg} ${st.text}`}>{st.label}</span>
                </div>
                <p className="text-[11px] text-zinc-500 mt-0.5">Ref {v.reference} · {new Date(v.submitted_at).toLocaleDateString()}</p>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
